import { RobotOutlined } from '@ant-design/icons'
import { Button, Input, InputNumber, Select, Space, Typography, message } from 'antd'
import { useEffect, useState } from 'react'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'

const { Paragraph, Title, Text } = Typography

const AGENT_OPTIONS = [
	{ label: 'Product Owner', value: 'product_owner' },
]

const INITIAL_FORM = {
	objective: '',
	systemPrompt: '',
	outputSchema: '',
	knowledgeDocPath: '',
	model: '',
	temperature: 0.4,
	maxOutputTokens: 0,
}

export const AgentsConfigs = () => {
	const [agentKey, setAgentKey] = useState('product_owner')
	const [form, setForm] = useState(INITIAL_FORM)
	const [currentVersion, setCurrentVersion] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isSaving, setIsSaving] = useState(false)

	const loadConfig = async selectedAgentKey => {
		setIsLoading(true)
		try {
			const rs = await api.agentsAdminApi.getActiveAgentConfig(selectedAgentKey)
			setForm({
				objective: rs?.objective || '',
				systemPrompt: rs?.systemPrompt || '',
				outputSchema: rs?.outputSchemaRaw || '{}',
				knowledgeDocPath: rs?.knowledgeDocPath || '',
				model: rs?.model || '',
				temperature:
					typeof rs?.temperature === 'number' ? rs.temperature : 0.4,
				maxOutputTokens: rs?.maxOutputTokens || 0,
			})
			setCurrentVersion(rs?.version || null)
		} catch (err) {
			setForm(INITIAL_FORM)
			setCurrentVersion(null)
			message.error(err?.message || 'Could not load active agent config.')
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		loadConfig(agentKey)
	}, [agentKey])

	const handleSave = async () => {
		const objective = form.objective.trim()
		const systemPrompt = form.systemPrompt.trim()
		const outputSchema = form.outputSchema.trim() || '{}'
		const knowledgeDocPath = form.knowledgeDocPath.trim()
		const model = form.model.trim()
		const temperature =
			typeof form.temperature === 'number' ? form.temperature : 0
		const maxOutputTokens = Number(form.maxOutputTokens || 0)

		if (!objective || !systemPrompt) {
			message.warning('Objective and System prompt are required.')
			return
		}
		try {
			JSON.parse(outputSchema)
		} catch {
			message.warning('Output schema must be valid JSON.')
			return
		}

		setIsSaving(true)
		try {
			const updated = await api.agentsAdminApi.updateActiveAgentConfig(
				agentKey,
				{
					objective,
					systemPrompt,
					outputSchema,
					knowledgeDocPath,
					model,
					temperature,
					maxOutputTokens,
				}
			)
			setCurrentVersion(updated?.version || null)
			setForm(prev => ({
				...prev,
				outputSchema: updated?.outputSchemaRaw || prev.outputSchema,
			}))
			message.success('Agent config updated and activated.')
		} catch (err) {
			message.error(err?.message || 'Could not update agent config.')
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<ListPageShell>
			<ListPageIntro
				icon={RobotOutlined}
				title="Agent Configs"
				subtitle="Manage active prompts and model settings for each agent."
			/>
			<Space direction="vertical" size={16} style={{ width: '100%' }}>
				<div>
					<Title level={5}>Agent selection</Title>
					<Select
						value={agentKey}
						options={AGENT_OPTIONS}
						style={{ minWidth: 260 }}
						onChange={setAgentKey}
					/>
					<Paragraph type="secondary" style={{ marginTop: 8 }}>
						Active version: {currentVersion || 'N/A'}
					</Paragraph>
				</div>

				<div>
					<Title level={5}>Config</Title>
					<Space direction="vertical" style={{ width: '100%' }} size={10}>
						<div>
							<Text strong>Objective</Text>
							<Input.TextArea
								rows={3}
								value={form.objective}
								disabled={isLoading}
								onChange={e =>
									setForm(prev => ({
										...prev,
										objective: e.target.value,
									}))
								}
							/>
						</div>
						<div>
							<Text strong>System prompt</Text>
							<Input.TextArea
								rows={8}
								value={form.systemPrompt}
								disabled={isLoading}
								onChange={e =>
									setForm(prev => ({
										...prev,
										systemPrompt: e.target.value,
									}))
								}
							/>
						</div>
						<div>
							<Text strong>Output schema (JSON)</Text>
							<Input.TextArea
								rows={5}
								value={form.outputSchema}
								disabled={isLoading}
								onChange={e =>
									setForm(prev => ({
										...prev,
										outputSchema: e.target.value,
									}))
								}
							/>
						</div>
						<div>
							<Text strong>Knowledge document path</Text>
							<Input
								placeholder="Optional, e.g. @wordsmine-suite-ssot.md"
								value={form.knowledgeDocPath}
								disabled={isLoading}
								onChange={e =>
									setForm(prev => ({
										...prev,
										knowledgeDocPath: e.target.value,
									}))
								}
							/>
						</div>
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: 12,
								alignItems: 'flex-end',
							}}
						>
							<div style={{ minWidth: 320, flex: 2 }}>
								<Text strong style={{ display: 'block', marginBottom: 6 }}>
									Model override
								</Text>
								<Input
									placeholder="Optional, e.g. gemini-2.0-flash-001"
									value={form.model}
									disabled={isLoading}
									onChange={e =>
										setForm(prev => ({
											...prev,
											model: e.target.value,
										}))
									}
								/>
							</div>
							<div style={{ minWidth: 140, flex: 1 }}>
								<Text strong style={{ display: 'block', marginBottom: 6 }}>
									Temperature
								</Text>
								<InputNumber
									min={0}
									max={2}
									step={0.1}
									value={form.temperature}
									disabled={isLoading}
									onChange={value =>
										setForm(prev => ({
											...prev,
											temperature:
												typeof value === 'number' ? value : 0,
										}))
									}
									style={{ width: '100%' }}
								/>
							</div>
							<div style={{ minWidth: 180, flex: 1 }}>
								<Text strong style={{ display: 'block', marginBottom: 6 }}>
									Max output tokens
								</Text>
								<InputNumber
									min={0}
									step={64}
									value={form.maxOutputTokens}
									disabled={isLoading}
									onChange={value =>
										setForm(prev => ({
											...prev,
											maxOutputTokens:
												typeof value === 'number' ? value : 0,
										}))
									}
									style={{ width: '100%' }}
								/>
							</div>
						</div>
						<Button
							type="primary"
							onClick={handleSave}
							loading={isSaving}
							disabled={isLoading}
						>
							Save as new active version
						</Button>
					</Space>
				</div>
			</Space>
		</ListPageShell>
	)
}
